import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  getFramlAlertEvents,
  getFramlAlertScenarios
} from '../../../../../../redux/actions/tmCommon';
import {
  fetchAMLSAlertLevel,
  fetchTypologiesActions
} from '../../../../../../redux/actions/common';
import { fetchDefaultScoringConfig } from '../../../../../../redux/actions/trm/defaultScoringConfig';
import { AMLS_COMMON } from '../../../../../constants/globalConstants';
import EventFieldConfigModule from '../../../../../../services/apiModules/EventFieldConfigModule';
import ScoringConfigModule from '../../../../../../services/trm/apiModules/ScoringConfigModule';
import { TRM_CONSTANTS } from '../../../../../constants/trmConstants';
import { isMockFallbackEnabled, isMockForced } from '../../framlLayoutToggle';
import { toEvents } from './toEvents';
import { toIconConfig } from './toIconConfig';
import { toLegend, toScoreBands } from './toBands';
import { toScenarioList } from './toScenarioList';
import {
  toScenarioMeta,
  withScenarioMeta,
  toVariantMeta,
  withVariantMeta,
  withActionColor
} from './toScenarioMeta';
import EventTimeline from './EventTimeline';

// THE EVENT THAT RAISED THE ALERT, resolved from the alert itself rather than from
// the timeline: `isTrigger` is true for EVERY event in `contextMode=triggered`, so
// it cannot single out the one that created the alert.
//
// A FRAML-P alert is raised by exactly ONE event, and which column holds its key
// depends on the kind (see the SmartAssessment top-panel contract):
//   transaction -> unified_alerts.TRANSACTION_KEY, surfaced as `transactionKey`
//   activity    -> the `activity_*` fields inside `additionalInfo`
// Both are live on GET /alerts/:alertId (verified: transactionKey QA-TXN-001-17,
// typeOfAlert "transaction"), and the value matches the timeline event `id`.
const toTriggerEventId = (alertDetails) => {
  if (!alertDetails || typeof alertDetails !== 'object') return undefined;

  if (alertDetails.typeOfAlert === 'activity') {
    // additionalInfo is populated only on activity alerts, and arrives as a JSON
    // string on some paths.
    const raw = alertDetails.additionalInfo;
    let info = raw;
    if (typeof raw === 'string') {
      try {
        info = JSON.parse(raw);
      } catch (e) {
        return undefined;
      }
    }
    const id = info && (info.activity_id || info.activityId);
    return typeof id === 'string' && id ? id : undefined;
  }

  const key = alertDetails.transactionKey;
  return typeof key === 'string' && key ? key : undefined;
};

// The event-type configuration, for the admin's chosen icons only (FIN-13583).
// Module scope so the instance is shared rather than rebuilt per mount — it holds
// nothing but a base URL and a header.
const iconConfigApi = new EventFieldConfigModule(AMLS_COMMON, {
  'Content-Type': 'application/json'
});

// The alert's own scenario scoring config (FIN-13569) — same module and base URL
// the trm services use. Module scope for the same reason as iconConfigApi.
const scoringConfigApi = new ScoringConfigModule(
  TRM_CONSTANTS.TRM_BASE_URL,
  TRM_CONSTANTS.HTTP_HEADERS
);

/** Thresholds a band can be built from: both cut-offs finite and in order. */
const usableThresholds = (thresholds) => {
  const lowMax = Number(thresholds?.lowMax);
  const mediumMax = Number(thresholds?.mediumMax);
  return (
    Number.isFinite(lowMax) && Number.isFinite(mediumMax) && lowMax < mediumMax
  );
};

// Container for the Spatial event timeline (BE-1 / FIN-11364).
//
// Live data: GET /amls/common/alerts/{alertId}/eventTimeline via the existing
// getFramlAlertEvents action; the response is mapped by ./toEvents.
//
// Same split as the other Spatial panels — this file owns redux, EventTimeline.js
// owns rendering, and the mapping is a separate pure function so it can be tested
// without a store or a network.
const EventTimelineContainer = ({
  alertId,
  events,
  isLoading,
  error,
  fetchEvents,
  scenariosPayload,
  fetchScenarios,
  scoreExplainability,
  alertScoreExplanation,
  alertDetails,
  fallbackSequence,
  selectedId,
  onSelect,
  expanded,
  onExpandedChange,
  // FIN-13569 — the configured severity bands. Taken as props rather than read
  // from the store here so the mapping stays testable without one.
  alertLevels,
  scoringThresholds,
  trmActions,
  ...props
}) => {
  // The selection is stored WITH the alert it belongs to, and the effective index
  // is derived from it. Holding a bare index in state let a stale one survive an
  // alertId change for one render — long enough to fire a timeline request for the
  // PREVIOUS alert's variant before the reset landed. Deriving makes the reset
  // simultaneous with the alert change rather than a follow-up effect.
  const [selection, setSelection] = useState({ alertId: null, index: 0 });
  const scenarioIndex = selection.alertId === alertId ? selection.index : 0;

  // CONTEXT MODE IS ALWAYS `triggered` (2026-08-21, user's call — and it is what
  // PRD §1 describes: "The Spatial View must display CONTRIBUTING events").
  //
  // `full` was used for the unfiltered view, on the reasoning that a bare list of
  // triggering events loses the benign context the design was drawn around. But it
  // returns the party's whole recent history — 60 events on QA-FRAML-001 against
  // 48 contributing — which is not what this surface is for, and the BE ignores
  // the variant filter in that mode anyway.
  //
  // So "All events" now means "every event contributing to this alert", and a
  // selected scenario narrows that to one variant. The variant counts are subsets
  // of the unfiltered total, which is also why they can overlap without the sum
  // matching.
  const CONTEXT_MODE = 'triggered';

  useEffect(() => {
    if (!alertId) return;
    // ONLY the scenario list here. The events fetch now waits for it, because the
    // view opens on the FIRST VARIANT (team lead, 2026-08-21) and the variant name
    // is the filter — there is nothing to request until this resolves.
    //
    // This reverses the earlier "the selector must never delay the primary
    // content" note: that held while an unfiltered "All events" row existed to
    // show immediately. With the synthetic entry gone, an unfiltered fetch would
    // only be thrown away a moment later.
    fetchScenarios({ alertId });
  }, [alertId]);

  // The variant / Score / Action beside the dropdown. NOT from the scenarios
  // endpoint — it does not carry them (see toScenarioMeta) — but joined from the
  // two Explanation slices, so no new API and no second derivation of a score
  // that the Explanation table already shows.
  //
  // READ-ONLY, BY DESIGN: this container does not dispatch those two fetches.
  // ExplanationPanelContainer already does, and Explanation is the default tab, so
  // on a normal page load the data is in the store by the time the bar needs it.
  // Dispatching here as well would fire a duplicate request on EVERY load to
  // cover one case: a deep link straight to another tab (?tab=activity), where
  // Explanation never mounts and the tags stay hidden until it is opened. Hidden
  // is the pre-existing behaviour and costs nothing; two extra requests per load
  // for every user would not be.
  const scenarioMeta = useMemo(
    () => toScenarioMeta(scoreExplainability, alertScoreExplanation),
    [scoreExplainability, alertScoreExplanation]
  );

  // Per-VARIANT score / action / typology-id, keyed by variant name. Fills what
  // the scenarios endpoint could not resolve — 4 of 6 variants on QA-FRAML-001,
  // every activity-driven one structurally. Gaps only; a value the endpoint
  // resolved always wins. See toVariantMeta.
  const variantMeta = useMemo(
    () => toVariantMeta(alertScoreExplanation, scoreExplainability),
    [alertScoreExplanation, scoreExplainability]
  );

  const scenarios = useMemo(
    () =>
      withActionColor(
        withVariantMeta(
          withScenarioMeta(toScenarioList(scenariosPayload), scenarioMeta),
          variantMeta
        ),
        scoreExplainability,
        trmActions,
        alertLevels
      ),
    [
      scenariosPayload,
      scenarioMeta,
      variantMeta,
      scoreExplainability,
      trmActions,
      alertLevels
    ]
  );

  // Once the variants arrive, load the selected one (index 0 on open). An alert
  // with NO variants still gets its timeline — unfiltered — rather than an empty
  // row: no scenario to narrow by is not the same as no events.
  const requestedFor = useRef(null);
  useEffect(() => {
    if (!alertId || !scenariosPayload) return;
    // STALE-SCENARIO GUARD. Switching alerts dispatches a scenarios fetch, but
    // the slice still holds the PREVIOUS alert's variants until that resolves.
    // Without this, the effect fires immediately and asks the new alert for the
    // old alert's rule — a request that can only come back wrong, and is then
    // superseded a moment later. AlertScenariosResponse carries its own alertId,
    // so the mismatch is detectable; wait for the matching payload.
    if (scenariosPayload.alertId && scenariosPayload.alertId !== alertId)
      return;
    const selected = scenarios[scenarioIndex];
    const key = `${alertId}|${selected ? selected.variantName : ''}`;
    if (requestedFor.current === key) return;
    requestedFor.current = key;
    fetchEvents({
      alertId,
      contextMode: CONTEXT_MODE,
      ...(selected?.variantName ? { variantName: selected.variantName } : {})
    });
  }, [alertId, scenariosPayload, scenarios, scenarioIndex]);

  const activeScenario = scenarios[scenarioIndex];

  const handleScenarioSelect = (nextIndex) => {
    const next = scenarios[nextIndex];
    if (!next || nextIndex === scenarioIndex) return;
    // Only moves the index — the effect above owns the refetch, so selection and
    // first load go through exactly one code path.
    setSelection({ alertId, index: nextIndex });
  };

  // An explicit ?framlMock=on hands the whole view to the fixture, which carries
  // its OWN scenarios (with eventIds) and filters them client-side — so it keeps
  // ownership of the selection too. Hoisted out of the memo below because the
  // controlled-props decision needs it as well.
  const isFixtureForced = isMockForced() && Boolean(fallbackSequence);

  /**
   * NO *FIELD*-CONFIG FETCH HERE ANY MORE — but the ICON still needs one.
   *
   * The timeline used to load the per-type FIELD configuration itself. Since
   * FIN-12628 the BE resolves that server-side and embeds the result in every
   * event's `detail.fields[]`, so there is nothing to fetch and no window where
   * a node renders before its field configuration arrives.
   *
   * `icon_key` was left out of that work: `detail` is `{ eventType, fields }`
   * and carries no `iconKey`, so the admin's chosen glyph never reached the view
   * (FIN-13583). Adding it to the timeline payload is the tidier fix and is
   * worth raising, but the config endpoint is already there, already gated on
   * the same `commonScopeRead` an analyst holds to open this alert, and needs no
   * backend release — so the icon is fetched here.
   *
   * WHY THE OLD OBJECTION DOES NOT APPLY. The field fetch was removed partly
   * because nodes rendered before their configuration landed and then changed
   * shape — text appearing and moving under the reader. An icon cannot do that:
   * it occupies a fixed circle, the built-in glyph is already correct for every
   * configured type, and a late swap changes a picture in place rather than
   * reflowing evidence. A failure is silent by design (`toIconConfig` yields
   * `{}`) and simply leaves the built-in glyphs, which is the behaviour that
   * shipped before this call existed.
   */
  const [iconConfig, setIconConfig] = useState({});

  useEffect(() => {
    let live = true;

    // ONE CALL PER CATEGORY — `category` IS REQUIRED, not optional. The
    // controller answers a request without it with
    // `400 category query param is required`
    // (EventFieldConfigController:28), so a single argument-less call fetched
    // nothing and, because the failure path here is deliberately silent, did so
    // invisibly: every node kept its built-in glyph and no error reached the
    // console. Found only by opening the alert.
    //
    // Each category is caught on its own, so a failure in one still lets the
    // other's icons render. The two results are merged rather than concatenated:
    // `toIconConfig` keys by category, so the objects cannot collide.
    const fetchCategory = (category) =>
      iconConfigApi.getEventTypes(category).catch(() => []);

    Promise.all([fetchCategory('ACTIVITY'), fetchCategory('TRANSACTION')]).then(
      ([activity, transaction]) => {
        if (!live) return;
        setIconConfig({
          ...toIconConfig(activity),
          ...toIconConfig(transaction)
        });
      }
    );

    // Guards a setState after the panel closes mid-flight.
    return () => {
      live = false;
    };
  }, []);

  /**
   * THE SEVERITY LEGEND IS CONFIGURATION, NOT COPY (FIN-13569).
   *
   * Its names and cut-offs were hardcoded as High (70+) / Medium (40–69) /
   * Low (<40) — a middle band whose name is not one of the three codes, and
   * boundaries that match nothing a tenant can set. Both halves are fetched
   * here, from the two places the configuration actually lives.
   *
   * FETCHED ONLY IF ABSENT, which is the pattern the settings screens and
   * SmartAssessment already use: these are tenant-level and change about never,
   * so a second alert must not refetch them.
   *
   * THE BADGES MOVE WITH THE LEGEND. `bands` goes to `toEvents` and to the nodes
   * as well, because a legend claiming 66–100 beside a badge that bands 68 as
   * Medium is worse than either being wrong alone — the screen would contradict
   * itself in one glance.
   */
  useEffect(() => {
    // `alertId` routes both reads to the alert read scope every alert viewer
    // holds; without it they needed Settings permissions, and an analyst got a
    // 403 and a silent 33/66 fallback. See the `?alertId=` aliases.
    if (!alertLevels?.length) props.fetchAlertLevels(alertId);
    if (!scoringThresholds) props.fetchScoringConfig(alertId);
    // FIN-13724 — the Action tag's fallback severity. Classic's header loads
    // this; Spatial never mounts it, so without this the colour depended on
    // whether Classic had been opened first. Same guard as Classic's.
    if (!trmActions?.length && props.fetchTrmActions)
      props.fetchTrmActions(alertId);
    // Tenant-level and effectively immutable; re-running per alert would be a
    // request per navigation for a value that cannot have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * THE ALERT'S OWN SCENARIO WINS (FIN-13569). The backend bands an alert with
   * its scenario's scoring config when one is set — the whole config, not
   * merged — and only otherwise with the tenant default. The legend must use the
   * same cut-offs or the header's level and the node badges can disagree.
   *
   * Kept LOCAL, not in the store: it is per scenario, and the shared
   * scenario-config slice belongs to the configuration screen.
   *
   * A null body (no override), a failure, or an unusable config all fall back to
   * the tenant default. Note the backend levels an alert when its scenario
   * RUNS, so a threshold edited since then can still differ from an old alert.
   */
  // `unified_alerts.scenario_id` — the TRM scenario id the fraud persister writes
  // (FraudAlertPersisterProcessFunction: scenarioId = value.scenarioId), exposed
  // on GET /alerts/{alertId}. Some synthetic/blacklist writers store 0 or -1,
  // which name no scenario, so only a positive id is looked up.
  const scenarioId =
    Number(alertDetails?.scenarioId) > 0 ? alertDetails.scenarioId : undefined;
  const [scenarioConfig, setScenarioConfig] = useState({
    scenarioId: null,
    thresholds: null
  });

  useEffect(() => {
    if (!alertId || !scenarioId) return undefined;
    let live = true;
    scoringConfigApi
      .getScoringConfig({ scenarioId, alertId })
      .then((config) => {
        if (live) setScenarioConfig({ scenarioId, thresholds: config?.thresholds });
      })
      .catch(() => {
        if (live) setScenarioConfig({ scenarioId, thresholds: null });
      });
    return () => {
      live = false;
    };
  }, [alertId, scenarioId]);

  // Only the override fetched FOR THIS scenario counts — a previous alert's must
  // not colour this one while the new request is in flight.
  const scenarioThresholds =
    scenarioConfig.scenarioId === scenarioId ? scenarioConfig.thresholds : null;

  const bands = useMemo(
    () =>
      toScoreBands(
        usableThresholds(scenarioThresholds)
          ? scenarioThresholds
          : scoringThresholds
      ),
    [scenarioThresholds, scoringThresholds]
  );

  const legend = useMemo(() => toLegend(alertLevels, bands), [
    alertLevels,
    bands
  ]);

  const triggerEventId = toTriggerEventId(alertDetails);

  const sequence = useMemo(() => {
    const mapped = toEvents(events, triggerEventId, iconConfig, bands);

    // An EXPLICIT `?framlMock=on` (or the localStorage flag) FORCES the fixture,
    // even when live data is present. Without this the fixture is unreachable the
    // moment the BE returns anything — which is exactly the state we are in now
    // that StarRocks is seeded, and it makes design work that only exists in the
    // fixture (the SCENARIO selector) impossible to preview or demo.
    // Deliberately narrower than isMockFallbackEnabled(): only the explicit
    // override forces, never the implicit "previewing the redesign" one, so
    // ?framlLayout=new on its own still shows REAL data.
    if (isFixtureForced) return fallbackSequence;

    // The scenarios on THIS response are dropped and replaced by the ones from
    // /alerts/:alertId/scenarios. The timeline payload has no `scenarios` field in
    // the shipped contract (verified: alertId, contextMode, eventCount, truncated,
    // events) — anything appearing here is a prototype build, and the dedicated
    // endpoint is the only source we render.
    const { scenarios: ignoredInlineScenarios, ...live } = mapped;

    if (mapped.events.length) return { ...live, scenarios };

    // DEV-ONLY fallback, and only while the dev toggle is on — never because a
    // live response came back empty. An alert that legitimately has no events
    // must render the empty state rather than a fabricated sequence; see the
    // note in framlLayoutToggle.js.
    //
    // Suppressed once the alert HAS live scenarios: filtering to a typology with
    // no rows is a legitimate 200 with eventCount 0, and swapping in fabricated
    // events at that moment would both hide the empty result and replace the live
    // selector with the fixture's own — leaving no way back.
    if (isMockFallbackEnabled() && fallbackSequence && !scenarios.length) {
      return fallbackSequence;
    }

    return { ...mapped, scenarios };
  }, [
    events,
    fallbackSequence,
    scenarios,
    isFixtureForced,
    triggerEventId,
    iconConfig,
    bands
  ]);

  // Controlled ONLY when live scenarios exist and the fixture has not been
  // forced. The fixture carries eventIds and filters client-side, so it keeps its
  // own index and behaves exactly as before.
  const isControlled = scenarios.length > 0 && !isFixtureForced;

  return (
    <EventTimeline
      sequence={sequence}
      isLoading={isLoading}
      error={error}
      selectedId={selectedId}
      onSelect={onSelect}
      selectedScenarioIndex={isControlled ? scenarioIndex : undefined}
      onScenarioSelect={isControlled ? handleScenarioSelect : undefined}
      /* FIN-13570 — FORWARDED, not consumed. This container hands the
         presentational component an explicit prop list rather than spreading,
         so anything the shell adds has to be added here too or it is silently
         dropped. That is what happened first time round: the shell owned the
         maximise state, the timeline never received it, and the button went
         back to doing nothing. */
      legend={legend}
      bands={bands}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    />
  );
};

EventTimelineContainer.propTypes = {
  alertId: PropTypes.string,
  // Pass-through to EventTimeline; see the note at the render above.
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  // Raw EventTimelineItem[] from the endpoint.
  events: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  fetchEvents: PropTypes.func.isRequired,
  // Raw AlertScenariosResponse from /alerts/:alertId/scenarios.
  scenariosPayload: PropTypes.object,
  fetchScenarios: PropTypes.func.isRequired,
  // The two Explanation slices, read (never fetched) here to source the scenario
  // bar's variant / Score / Action. See the note in the component.
  scoreExplainability: PropTypes.object,
  alertScoreExplanation: PropTypes.object,
  // The live alert, read for the triggering event's key. See toTriggerEventId.
  alertDetails: PropTypes.object,
  // Mock fixture passed down by the shell; used only when the dev toggle is on.
  fallbackSequence: PropTypes.object,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  // FIN-13569 — the legend's tenant-level halves, and how to fetch them (with
  // the alert id, so any alert viewer may read them).
  alertLevels: PropTypes.array,
  trmActions: PropTypes.array,
  scoringThresholds: PropTypes.object,
  fetchAlertLevels: PropTypes.func,
  fetchScoringConfig: PropTypes.func,
  fetchTrmActions: PropTypes.func
};

const mapStateToProps = (state) => {
  const tmCommon = state.amlsTMCommon || {};
  const { framlAlertEvents = {}, framlAlertScenarios = {} } = tmCommon;
  return {
    events: framlAlertEvents.data,
    isLoading: framlAlertEvents.isLoading,
    error: framlAlertEvents.error,
    // Only the data: a failed or pending scenarios call must not put the timeline
    // into a loading or error state, so those fields are deliberately not mapped.
    scenariosPayload: framlAlertScenarios.data,
    // Same two slices ExplanationPanelContainer reads, and for the same alert —
    // the scenario bar's tags and the Explanation table are then two views of one
    // payload rather than two derivations that can disagree.
    scoreExplainability: tmCommon.preventionScoreExplainability?.data,
    alertScoreExplanation: tmCommon.alertScoreExplanation?.data,
    // Same slice the FP header reads transactionKey from.
    alertDetails: (state.amlsCommon || {}).alertDetails?.data,
    // FIN-13569 — the legend's two halves. Names, colours and codes come from
    // /common/alertLevelClassification; the cut-offs are the same pair the
    // Settings → Scoring slider edits.
    alertLevels: (state.amlsCommon || {}).alertLevelConfigs?.data,
    // FIN-13724 — Action Catalog, the Action tag colour's fallback severity.
    // Read-only, same slice Classic's Explanation reads.
    trmActions: (state.amlsCommon || {}).commonTypologyAction?.data,
    scoringThresholds: state.defaultScoringConfig?.data?.thresholds
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchEvents: (payload) => dispatch(getFramlAlertEvents(payload)),
  fetchScenarios: (payload) => dispatch(getFramlAlertScenarios(payload)),
  fetchAlertLevels: (alertId) =>
    dispatch(fetchAMLSAlertLevel(alertId ? { alertId } : undefined)),
  fetchTrmActions: (alertId) =>
    dispatch(fetchTypologiesActions(alertId ? { alertId } : undefined)),
  fetchScoringConfig: (alertId) =>
    dispatch(fetchDefaultScoringConfig(alertId ? { alertId } : undefined))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventTimelineContainer);
